<template>
	<div>
		<div class="flex w-full flex-row justify-between">
			<div class="my-auto">
				<p class="my-auto text-base">
					{{ outlet.content.address }}
				</p>

				<p class="my-auto text-base">
					{{ outlet.content.postal }} {{ outlet.content.city }}
				</p>
			</div>

			<div v-if="outlet.content.location" class="py-sm">
				<v-button
					:to="routeLink"
					class="px-xl py-xs"
					size="sm"
					target="_blank"
					theme="black"
					:aria-label="t('outlet-route')"
				>
					{{ t("outlet-route") }}
				</v-button>
			</div>
		</div>

		<div class="mt-md">
			<p>
				{{ t("outlet-call") }}

				<a :href="`tel:${outlet.content.phone}`" class="text-black underline">
					{{ outlet.content.phone }}
				</a>
			</p>
		</div>

		<div
			v-if="outlet.content.email && outlet.content.email.length > 1"
			class="mt-md"
		>
			<p>
				{{ t("outlet-email") }}

				<a
					:href="`mailto:${outlet.content.email}`"
					class="text-black underline"
				>
					{{ outlet.content.email }}
				</a>
			</p>
		</div>

		<nuxt-link :to="'/' + outlet.full_slug" :aria-label="outlet.name">
			<p class="mt-md underline">{{ t("outlet-info") }}</p>
		</nuxt-link>

		<div class="mt-9 gap-y-xs">
			<p class="font-sans-caption font-bold">{{ t("outlet-hours") }}</p>

			<table
				v-if="outlet.content.hours_table"
				class="my-md w-full table-auto lg:w-2/3"
			>
				<thead>
					<tr>
						<th
							v-for="th in outlet.content.hours_table.thead"
							:key="th"
							class="pb-xs text-left"
						>
							{{ th.value }}
						</th>
					</tr>
				</thead>

				<tbody>
					<tr
						v-for="(tr, index) in outlet.content.hours_table.tbody"
						:key="index"
						class="space-y-md"
					>
						<td
							v-for="td in tr.body"
							:key="td"
							class="pb-xs"
							:class="{
								'font-bold': tr.body[0].value === currentDayUpperCase,
							}"
						>
							{{ td.value }}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="prose">
				<p v-if="outlet.content.hours.content.length > 0" class="font-bold">
					{{ t("exceptions") }}
				</p>

				<sb-rich-text :document="outlet.content.hours" />
			</div>
		</div>

		<div v-if="outlet.content.outlet_body" class="prose pt-lg">
			<sb-rich-text :document="outlet.content.outlet_body" />
		</div>

		<div
			v-if="outlet.content.warehouse_hours?.content?.[0].content"
			class="mt-9 mb-lg flex flex-col gap-y-xs"
		>
			<p class="font-sans-caption font-bold">
				{{ t("outlet-warehouse-hours") }}
			</p>

			<div class="prose">
				<sb-rich-text :document="outlet.content.warehouse_hours" />
			</div>
		</div>

		<div v-for="(lang, index) in outlet.content.languages" :key="index">
			<sb-languages :blok="lang" />
		</div>
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";

const properties = defineProps<{
	outlet: ISbStoryData;
}>();

const {t} = useI18n();

const currentDay = new Intl.DateTimeFormat(t("date-locale"), {
	weekday: "long",
}).format();
const currentDayUpperCase =
	currentDay.charAt(0).toUpperCase() + currentDay.slice(1);

const routeLink = computed(
	() =>
		`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(properties.outlet.content.location)}`,
);
</script>

<i18n lang="json">
{
	"et": {
		"date-locale": "et-ET",
		"outlet-route": "Juhised",
		"outlet-call-price": "Kõne hind (sis. Km)",
		"outlet-info": "Lisainfo kaupluse lehel",
		"outlet-hours": "Lahtiolekuajad",
		"outlet-warehouse-hours": "Ladu avatud",
		"outlet-warehouse": "Laod",
		"outlet-nearest": "Lähim kauplus",
		"outlet-call": "Helista",
		"outlet-location": "Kaupluse asukoht",
		"closed": "Suletud",
		"exceptions": "Erandid",
		"outlet-email": "Saada e-post"
	},
	"fi": {
		"date-locale": "fi-FI",
		"outlet-call": "Soita",
		"outlet-hours": "Avoinna",
		"outlet-route": "Reitti",
		"outlet-warehouse-hours": "Varaston noutoajat",
		"outlet-info": "Lisätietoa myymälän sivuilta",
		"outlet-call-price": "Puhelun hinta 0,11 €/min (sis. alv)",
		"outlet-warehouse": "Varastot",
		"outlet-nearest": "Lähin myymäläsi",
		"outlet-location": "Myymälän sijanti",
		"closed": "Suljettu",
		"exceptions": "Poikkeukset",
		"outlet-email": "Lähetä sähköpostia"
	}
}
</i18n>
